import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import dayjs from 'dayjs';
import { APIURL, BINGAPIKEY } from '../../../config';
import { useHistory, Redirect } from 'react-router-dom';
import { Box, Alert, Button, Tabs, Tab } from '@mui/material';
import PersonalDetails from '../CustomerForm/PersonalDetails/PersonalDetails';
import OtherDetails from './OtherDetails/OtherDetails';
import CustomerTasks from './CustomerTasks/CustomerTasks';
import MsgModalBox from '../../../container/MessageModalBox/MsgModalBox';
import { normalizeGender, addressFromParts } from '../../../utils/customerFields';

const CustomerForm = (props) => {
  const history = useHistory();
  const [schemaLocal, setSchemaLocal] = useState(props.schema);
  const [schemaDefaultsLocal, setSchemaDefaultsLocal] = useState(props.schemaDefaults);
  // Fields default to visible/optional until the company's schema arrives, so hold the
  // form back until then -- otherwise every field flashes before the real layout appears.
  const [schemaLoading, setSchemaLoading] = useState(!props.schema || !props.schemaDefaults);
  const getSchemaInfo = (field) => {
    const info = schemaLocal?.find(s => s.columnName?.toLowerCase() === field.toLowerCase())
      || schemaDefaultsLocal?.find(s => s.columnName?.toLowerCase() === field.toLowerCase())
      || {};
    return info;
  };
  const isFieldRequired = (field) => {
    const info = getSchemaInfo(field);
    return info.columnOptional !== undefined ? !info.columnOptional : false;
  };
  const isFieldVisible = (field) => {
    const info = getSchemaInfo(field);
    // If columnVisible is undefined, default to true (show field)
    return info.columnVisible !== undefined ? !!info.columnVisible : true;
  };
  const getFieldLabel = (field, fallback) => getSchemaInfo(field).columnLabel || fallback;

  const [personalDetails, setPersonalDetails] = useState(() => {
    return {
      DDDID: !props.edit ? '' : props.cusDets.DDDID,
      cus_is_company: !props.edit ? false : props.cusDets.cus_is_company,
      first_name: !props.edit ? '' : !props.cusDets.cus_is_company ? (props.cusDets.cus_first_name || props.cusDets.cus_name.split(' ')[0]) : ' ',
      last_name: !props.edit ? '' : !props.cusDets.cus_is_company ? (props.cusDets.cus_last_name || props.cusDets.cus_name.split(' ').slice(1).join(' ')) : ' ',
      gender: !props.edit ? 'M' : normalizeGender(props.cusDets.gender),
      dob: !props.edit || !props.cusDets.dob ? null : props.cusDets.dob,
      company_name: !props.edit ? '' : !props.cusDets.cus_is_company ? ' ' : props.cusDets.cus_name,
      cus_local_id: !props.edit ? '' : props.cusDets.cus_local_id,
      email: !props.edit ? '' : props.cusDets.cus_email,
      addressList: !props.edit
        ? [{ address_line_1: '', address_line_2: '', city: '', state: '', zip: '', latitude: '', longitude: '' }]
        : props.cusDets.cus_address.split(':').map((add, index) => {
            // Simple US state name to abbreviation mapping
            const stateMap = {
              'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD', 'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY'
            };
            let addArr = add.split(',');
            if (addArr.length > 5) {
              addArr = addArr.map((data, i) => {
                if (i > 1) {
                  return data;
                } else if (i < 1) {
                  return data + ',' + addArr[i + 1];
                } else {
                  return null;
                }
              }).filter((ele) => !!ele);
            }
            // Normalize state to abbreviation for matching
            function normalizeState(val) {
              if (!val) return '';
              const v = val.trim().toLowerCase();
              return stateMap[v] || val.trim().toUpperCase();
            }
            let location = null;
            try {
              if (addArr.length === 4) {
                location = props.loc_list.find((loc) => (
                  loc.location_addr1.trim().toLowerCase() === addArr[0].trim().toLowerCase() &&
                  normalizeState(loc.location_state) === normalizeState(addArr[2]) &&
                  loc.location_city.trim().toLowerCase() === addArr[1].trim().toLowerCase() &&
                  loc.location_zip.trim().toLowerCase() === addArr[3].trim().toLowerCase()
                ));
              }
              if (addArr.length === 5) {
                location = props.loc_list.find((loc) => {
                  return (
                    loc.location_addr1.trim().toLowerCase() === addArr[0].trim().toLowerCase() &&
                    loc.location_addr2.trim().toLowerCase() === addArr[1].trim().toLowerCase() &&
                    normalizeState(loc.location_state) === normalizeState(addArr[3]) &&
                    loc.location_city.trim().toLowerCase() === addArr[2].trim().toLowerCase() &&
                    loc.location_zip.trim().toLowerCase() === addArr[4].trim().toLowerCase()
                  );
                });
              }
            } catch (err) {
              location = '';
            }
            if (!!location) {
              let addObj = {
                address_line_1: location.location_addr1,
                address_line_2: location.location_addr2,
                city: location.location_city.trim(),
                state: location.location_state.trim(),
                zip: location.location_zip.trim(),
                latitude: !!location ? location.location_lat : 0,
                longitude: !!location ? location.location_lon : 0,
                disableFields: !props.tasklist ? false : props.tasklist.some((task) => ((task.tas_is_active === 0 || task.tas_is_active === 1) && task.tas_loc_id === location.id))
              };
              return addObj;
            } else {
              return addressFromParts(addArr);
            }
          }).filter((loc) => !!loc),
      phnNumList: !props.edit ? [{ phnId: 1, country_code: '+1', phone: '' }] : !!props.cusDets.cus_phone ? props.cusDets.cus_phone.split(',').map((phn, index) => {
        // Remove all non-digit characters except leading +
        let cleaned = phn.trim();
        let countryCode = '+1';
        let phoneNumber = '';
        
        if (cleaned.startsWith('+')) {
          // Extract country code (assumes +1 or +XX format)
          countryCode = cleaned.slice(0, 2);
          phoneNumber = cleaned.slice(2).replace(/\D/g, '');
        } else {
          // No country code present, clean the entire string
          phoneNumber = cleaned.replace(/\D/g, '');
        }
        
        let phnObj = { phnId: index, country_code: countryCode, phone: phoneNumber };
        return phnObj;
      }) : [{ phnId: 1, country_code: '+1', phone: '' }],
      mailing_detail: props.cusDets?.mailing_detail ? props.cusDets.mailing_detail : null
    };
  });
  const [otherDetails, setOtherDetails] = useState(() => {
    return {
      acquired_date: !props.edit ? moment().format('YYYY-MM-DDTHH:mm:ss') : props.cusDets.cus_acquired_date,
      status: !props.edit ? 'Active' : props.cusDets.cus_status,
      customer_type: !props.edit ? 'Individual' : !!props.cusDets.cus_type ? props.cusDets.cus_type : 'Individual',
      pManager: !props.edit ? '' : props.pManagerId,
      private_payer_responsible: !props.edit ? '' : props.cusDets.private_payer ? props.cusDets.private_payer : null,
      medicaid_id: !props.edit ? '' : props.cusDets.medicaid_id ? props.cusDets.medicaid_id : null,
      medicaid_eligible: !props.edit ? '' : Number(props.cusDets.medicaid_eligible),
      sign_page_date: !props.edit ? '' : props.cusDets.sign_page_date || '',
      last_plan_approval_date: !props.edit ? '' : props.cusDets.last_plan_approval_date || '',
      last_assess_date: !props.edit ? '' : props.cusDets.last_assess_date || '',
      renewal_date: !props.edit ? '' : props.cusDets.renewal_date || '',
      DDD_tier: !props.edit ? '' : props.cusDets.DDD_tier ? props.cusDets.DDD_tier : null,
      diagnose_code: !props.edit ? '' : props.cusDets.PrimaryDiagnosisCode ? props.cusDets.PrimaryDiagnosisCode : null,
      func_criteria_met: !props.edit ? '' : Number(props.cusDets.func_criteria_met),
      waiver_program: !props.edit ? '' : props.cusDets.waiver_program ? props.cusDets.waiver_program : null,
      NJCATSelfcare: !props.edit ? '' : props.cusDets.NJCATSelfcare ? props.cusDets.NJCATSelfcare : null,
      NJCATBehavioral: !props.edit ? '' : props.cusDets.NJCATBehavioral ? props.cusDets.NJCATBehavioral : null,
      NJCATMedical: !props.edit ? '' : props.cusDets.NJCATMedical ? props.cusDets.NJCATMedical : null,
      NJCATFCA: !props.edit ? '' : props.cusDets.NJCATFCA ? props.cusDets.NJCATFCA : null,
      MedicaidEndDate: !props.edit ? '' : props.cusDets.MedicaidEndDate || '',
      AgencyAssignDate: !props.edit ? '' : props.cusDets.AgencyAssignDate || '',
      County: !props.edit ? '' : props.cusDets.County ? props.cusDets.County : null,
      Race: !props.edit ? '' : props.cusDets.Race ? props.cusDets.Race : null,
      Ethnicity: !props.edit ? '' : props.cusDets.Ethnicity ? props.cusDets.Ethnicity : null,
      place01char: !props.edit ? '' : props.cusDets.place01char ? props.cusDets.place01char : null,
      place02char: !props.edit ? '' : props.cusDets.place02char ? props.cusDets.place02char : null,
      place03char: !props.edit ? '' : props.cusDets.place03char ? props.cusDets.place03char : null,
      place04char: !props.edit ? '' : props.cusDets.place04char ? props.cusDets.place04char : null,
      place05char: !props.edit ? '' : props.cusDets.place05char ? props.cusDets.place05char : null,
      place06char: !props.edit ? '' : props.cusDets.place06char ? props.cusDets.place06char : null,
      place07char: !props.edit ? '' : props.cusDets.place07char ? props.cusDets.place07char : null,
      place08char: !props.edit ? '' : props.cusDets.place08char ? props.cusDets.place08char : null,
      place09date: !props.edit ? '' : props.cusDets.place09date ? props.cusDets.place09date : null,
      place10date: !props.edit ? '' : props.cusDets.place10date ? props.cusDets.place10date : null,
      place11date: !props.edit ? '' : props.cusDets.place11date ? props.cusDets.place11date : null,
      place12date: !props.edit ? '' : props.cusDets.place12date ? props.cusDets.place12date : null,
      place13number: !props.edit ? '' : props.cusDets.place13number ? props.cusDets.place13number : null,
      place14number: !props.edit ? '' : props.cusDets.place14number ? props.cusDets.place14number : null,
      place15number: !props.edit ? '' : props.cusDets.place15number ? props.cusDets.place15number : null,
      place16number: !props.edit ? '' : props.cusDets.place16number ? props.cusDets.place16number : null,
      HasPet: !props.edit ? '' : props.cusDets.HasPet !== null && props.cusDets.HasPet !== undefined ? Number(props.cusDets.HasPet) : null,
      ProviderGenderPreference: !props.edit ? '' : props.cusDets.ProviderGenderPreference ? props.cusDets.ProviderGenderPreference : null,
      LanguagePreference: !props.edit ? '' : props.cusDets.LanguagePreference ? props.cusDets.LanguagePreference : null,
      PreferRepeatProviders: !props.edit ? '' : props.cusDets.PreferRepeatProviders !== null && props.cusDets.PreferRepeatProviders !== undefined ? Number(props.cusDets.PreferRepeatProviders) : null,
      DriverLicenseNumber: !props.edit ? '' : props.cusDets.DriverLicenseNumber ? props.cusDets.DriverLicenseNumber : null,
      DLExpiryDate: !props.edit ? '' : props.cusDets.DLExpiryDate || '',
      DLIssueDate: !props.edit ? '' : props.cusDets.DLIssueDate || '',
      DLIssueState: !props.edit ? '' : props.cusDets.DLIssueState ? props.cusDets.DLIssueState : null
    };
  });
  const [customerTasks, setCustomerTasks] = useState({});
  const [tabValue, setTabValue] = useState('personal_details');
  const [showModal, setShowModal] = useState(false);
  const [addError, setAddError] = useState(false);
  const [personalValid, setPersonalValid] = useState(false);
  const [otherValid, setOtherValid] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [goHome, setGoHome] = useState(false);
  const [personalErrors, setPersonalErrors] = useState({});
  const [otherErrors, setOtherErrors] = useState({});
  const [taskTypeList, setTaskTypeList] = useState([]);
  const [custTasksOriginal, setCustTasksOriginal] = useState([]);
  const [custTasksDDIds, setCustTasksDDIds] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [privatePayerSupportSetting, setPrivatePayerSupportSetting] = useState(false);

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const [schRes, defRes] = await Promise.all([
          axios.get(`${APIURL}/schema/table/customer`, { params: { companyID: props.comp_id } }),
          axios.get(`${APIURL}/schema/table-default/customer`, {
            params: {
              companyState: props.user.comp_settings.find(s => s.feature_id === 99)?.state_id || null,
              companyTypeOfBusiness: props.user.comp_settings.find(s => s.feature_value === 'primaryBusinessType')?.feature_id || null,
            }
          })
        ]);
        setSchemaLocal(schRes.data);
        setSchemaDefaultsLocal(defRes.data);
      } catch (err) {
        console.error('Failed to fetch schema for CustomerForm', err);
      } finally {
        setSchemaLoading(false);
      }
    };
    if (!props.schema || !props.schemaDefaults) fetchSchema();
  }, []);
  
  const loadTasksData = async () => {
    const res = await axios.get(`${APIURL}/customers/CustomerTasks`, { params: { comp_id: props.comp_id, customer_id: props.cusDets.cus_id } });
    const tasks = res.data;
    const contactsRes = await axios.get(`${APIURL}/contacts`, {params: {comp_id: props.comp_id, customer_id: props.cusDets.cus_id}});
    const cusContacts = contactsRes.data;
    setCustomerContacts(cusContacts);
    setCustTasksOriginal(tasks);
    setCustTasksDDIds(tasks.map(t => t.ddID));
    const typesRes = await axios.get(`${APIURL}/dropdowns/dropdownType/fullTaskType`, { params: { comp_id: props.comp_id } });
    setTaskTypeList(typesRes.data);
  };

  const getPrivatePayerSupportSetting = () => {
    props.user.comp_settings.find((f) => f.feature_id === 25) ? setPrivatePayerSupportSetting(true) : setPrivatePayerSupportSetting(false);
  }

  useEffect(() => {
    getPrivatePayerSupportSetting();
    if (props.edit) loadTasksData();
  }, []);

  useEffect(() => {
    if (props.edit && tabValue === 'customer_tasks' && taskTypeList.length === 0) {
      loadTasksData();
    }
  }, [tabValue]);

  const validatePersonal = () => {
    const errs = {};
    if (personalDetails.cus_is_company) {
      if (isFieldVisible('company_name') && !personalDetails.company_name?.trim()) errs['company_name'] = true;
    } else {
      if (isFieldVisible('first_name') && !personalDetails.first_name?.trim()) errs['first_name'] = true;
      if (isFieldVisible('last_name') && !personalDetails.last_name?.trim()) errs['last_name'] = true;
    }
    if (isFieldVisible('gender') && !personalDetails.gender) errs['gender'] = true;
    if (isFieldVisible('email') && !personalDetails.email?.trim()) errs['email'] = true;
    if (isFieldVisible('cus_local_id') && isFieldRequired('cus_local_id') && !personalDetails.cus_local_id?.trim()) errs['cus_local_id'] = true;
    if (isFieldVisible('dob') && !personalDetails.dob) errs['dob'] = true;
    if (isFieldVisible('address') && (!personalDetails.addressList.length || personalDetails.addressList.some(a => !a.address_line_1?.trim()))) errs['address'] = true;
    if (isFieldVisible('phone') && (!personalDetails.phnNumList.length || personalDetails.phnNumList.some(p => !p.phone?.trim()))) errs['phone'] = true;
    if (isFieldVisible('DDDID') && isFieldRequired('DDDID') && (!personalDetails.DDDID || props.customerList?.some(entry => entry.ddd_id === personalDetails.DDDID))) errs['DDDID'] = true;
    if (Object.keys(errs).length) {
      setPersonalErrors(errs);
      setShowAlert(true);
      return false;
    }
    return true;
  };

  const validateOther = () => {
    const errs = {};
    ['status','customer_type','pManager'].forEach(f => {
      if (isFieldVisible(f) && isFieldRequired(f) && !otherDetails[f]) errs[f] = true;
    });
    ['acquired_date','sign_page_date','last_plan_approval_date','last_assess_date','renewal_date','MedicaidEndDate','AgencyAssignDate','DLExpiryDate','DLIssueDate'].forEach(f => {
      if (isFieldVisible(f) && isFieldRequired(f) && (!otherDetails[f] || !moment(otherDetails[f]).isValid())) errs[f] = true;
    });
    ['medicaid_id','medicaid_eligible','DDD_tier','diagnose_code','func_criteria_met','waiver_program','NJCATSelfcare','NJCATBehavioral','NJCATMedical','NJCATFCA','County','Race','Ethnicity','TransferOutReason','HasPet','ProviderGenderPreference','LanguagePreference','PreferRepeatProviders','DriverLicenseNumber','DLIssueState'].forEach(f => {
      if (isFieldVisible(f) && isFieldRequired(f) && (otherDetails[f] === '' || otherDetails[f] === null || otherDetails[f] === undefined)) errs[f] = true;
    });
    ['place01char','place02char','place03char','place04char','place05char','place06char','place07char','place08char','place09date','place10date','place11date','place12date','place13number','place14number','place15number','place16number',].forEach(f => {
      if (isFieldVisible(f) && isFieldRequired(f) && (otherDetails[f] === '' || otherDetails[f] === null || otherDetails[f] === undefined)) errs[f] = true;
    });
    if (Object.keys(errs).length) {
      setOtherErrors(errs);
      setShowAlert(true);
      return false;
    }
    return true;
  };

  const changeTab = (event, newValue) => {
    if (tabValue === 'personal_details' && newValue !== 'personal_details') {
      if (!validatePersonal()) return;
    }
    if (tabValue === 'other_details' && newValue !== 'other_details') {
      if (!validateOther()) return;
    }
    setTabValue(newValue);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validatePersonal()) {
      setTabValue('personal_details');
      return;
    }
    if (!validateOther()) {
      setTabValue('other_details');
      return;
    }
    if (props.edit) {
      await editCustInDB(e);
    } else {
      await addCustomerToDB(e);
    }
  };

  const editCustInDB = async (e = {}) => {
    axios.defaults.headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept, Authorization',
      'Access-Control-Allow-Methods': 'PUT, GET, POST, DELETE, OPTIONS',
      'ACCESS-CONTROL-ALLOW-CREDENTIAL': true,
      Authorization: 'Bearer ' + window.localStorage.getItem('Fw4_access_Token')
    }
    var data = {
      empId: props.user.emp_id,
      empName: props.user.emp_name,
      empEmail: props.user.email,
      comp_id: props.comp_id,
      cus_id: props.cusDets.cus_id,
      cus_local_id: personalDetails.cus_local_id,
      private_payer: otherDetails.private_payer_responsible,
      DDDID: personalDetails.DDDID,
      cus_name: (personalDetails.company_name === " ") ? personalDetails.first_name + " " + personalDetails.last_name : personalDetails.company_name,
      first_name: personalDetails.cus_is_company ? '' : personalDetails.first_name,
      last_name: personalDetails.cus_is_company ? '' : personalDetails.last_name,
      gender: personalDetails.gender,
      dob: personalDetails.dob,
      email: (personalDetails.email !== 'undefined')? personalDetails.email: props.cusDets.cus_email ,
      addList: personalDetails.addressList,
      phonList: personalDetails.phnNumList.map((number) => { return number.country_code + number.phone }).join(),
      status: otherDetails.status,
      type: otherDetails.customer_type,
      medicaid_id: otherDetails.medicaid_id,
      medicaid_eligible: otherDetails.medicaid_eligible,
      sign_page_date: otherDetails.sign_page_date,
      last_assess_date: otherDetails.last_assess_date,
      last_plan_approval_date: otherDetails.last_plan_approval_date,
      renewal_date: otherDetails.renewal_date,
      proId: props.pId,
      DDD_tier: otherDetails.DDD_tier,
      diagnose_code:otherDetails.diagnose_code,
      func_criteria_met:otherDetails.func_criteria_met,
      acquired_date: moment(otherDetails.acquired_date).format('YYYY-MM-DDTHH:mm:ss'),
      waiver_program:otherDetails.waiver_program,
      cus_local_id: personalDetails.cus_local_id,
      NJCATSelfcare:otherDetails.NJCATSelfcare,
      NJCATBehavioral:otherDetails.NJCATBehavioral,
      NJCATMedical:otherDetails.NJCATMedical,
      NJCATFCA:otherDetails.NJCATFCA,
      MedicaidEndDate:otherDetails.MedicaidEndDate,
      AgencyAssignDate:otherDetails.AgencyAssignDate,
      County:otherDetails.County,
      Race:otherDetails.Race,
      Ethnicity:otherDetails.Ethnicity,
      TransferOutReason: otherDetails.TransferOutReason,
      TransferOutDate: otherDetails.TransferOutDate,
      mailing_detail: personalDetails.mailing_detail,
      place01char: otherDetails.place01char,
      place02char: otherDetails.place02char,
      place03char: otherDetails.place03char,
      place04char: otherDetails.place04char,
      place05char: otherDetails.place05char,
      place06char: otherDetails.place06char,
      place07char: otherDetails.place07char,
      place08char: otherDetails.place08char,
      place09date: otherDetails.place09date,
      place10date: otherDetails.place10date,
      place11date: otherDetails.place11date,
      place12date: otherDetails.place12date,
      place13number: otherDetails.place13number,
      place14number: otherDetails.place14number,
      place15number: otherDetails.place15number,
      place16number: otherDetails.place16number,
      HasPet: otherDetails.HasPet,
      ProviderGenderPreference: otherDetails.ProviderGenderPreference,
      LanguagePreference: otherDetails.LanguagePreference,
      PreferRepeatProviders: otherDetails.PreferRepeatProviders,
      DriverLicenseNumber: otherDetails.DriverLicenseNumber,
      DLExpiryDate: otherDetails.DLExpiryDate,
      DLIssueDate: otherDetails.DLIssueDate,
      DLIssueState: otherDetails.DLIssueState
    };
    let msg = await axios.put(APIURL+'/customers', data)
    if (msg.data.code === 200) {
      setShowModal(true);
      setAddError(false);
    } else {
      setAddError(true);
      setShowModal(true);
    }
  }

  const addCustomerToDB = async (e = {}) => {
    axios.defaults.headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept, Authorization',
      'Access-Control-Allow-Methods': 'PUT, GET, POST, DELETE, OPTIONS',
      'ACCESS-CONTROL-ALLOW-CREDENTIAL': true,
      Authorization: 'Bearer ' + window.localStorage.getItem('Fw4_access_Token')
    }
    const newState = {
      personal_details: {
        "first_name": "",
        "last_name": "",
        "gender": "",
        "dob": "",
        "email": "",
        "cus_local_id":"",
        "company_name": "",
        "DDDID": "",
        "mailing_detail": "",
        "addressList": [{ address_line_1: "", address_line_2: "", city: "", state: "", zip: "", latitude: "", longitude: "" }],
        "phnNumList": [{ phnId: 1, country_code: "+1", phone: "" }]
      },
      other_details: {
        "acquired_date": moment().format('YYYY-MM-DDTHH:mm:ss'),
        "status": "",
        "customer_type": "Individual",
        "pManager": "",
        "medicaid_id": "",
        "medicaid_eligible": "",
        "sign_page_date": "",
        "last_plan_approval_date": "",
        "last_assess_date": "",
        "renewal_date": "",
        "DDD_tier": "",
        "diagnose_code":"",
        "func_criteria_met":"",
        "waiver_program":"",
        "NJCATSelfcare":"",
        "NJCATBehavioral":"",
        "NJCATMedical":"",
        "NJCATFCA":"",
        "MedicaidEndDate":"",
        "AgencyAssignDate":"",
        "County":"",
        "Race":"",
        "Ethnicity":"",
        "place01chatr": "",
        "place02char": "",
        "place03char": "",
        "place04char": "",
        "place05char": "",
        "place06char": "",
        "place07char": "",
        "place08char": "",
        "place09date": "",
        "place10date": "",
        "place11date": "",
        "place12date": "",
        "place13number": "",
        "place14number": "",
        "place15number": "",
        "place16number": "",
        "HasPet": "",
        "ProviderGenderPreference": "",
        "LanguagePreference": "",
        "PreferRepeatProviders": "",
        "DriverLicenseNumber": "",
        "DLExpiryDate": "",
        "DLIssueDate": "",
        "DLIssueState": ""
      }
    }
    var data = {
      empId: props.user.emp_id,
      empName: props.user.emp_name,
      empEmail: props.user.email,
      company_id: props.comp_id,
      cus_id: props.edit ? props.cusDets.cus_id : "",
      cus_local_id: personalDetails.cus_local_id,
      DDDID: personalDetails.DDDID,
      is_company: personalDetails.cus_is_company,
      name: (!personalDetails.cus_is_company) ? personalDetails.first_name + " " + personalDetails.last_name : personalDetails.company_name,
      gender: personalDetails.gender,
      dob: personalDetails.dob,
      email: (personalDetails.email !== 'undefined')? personalDetails.email: props.cusDets.cus_email ,
      cus_local_id: personalDetails.cus_local_id,
      address: personalDetails.addressList,
      phone: personalDetails.phnNumList.map((number) => { return number.country_code + number.phone }).join(),
      status: otherDetails.status,
      customer_type: otherDetails.customer_type,
      medicaid_id: otherDetails.medicaid_id,
      medicaid_eligible: otherDetails.medicaid_eligible,
      sign_page_date: otherDetails.sign_page_date,
      last_assess_date: otherDetails.last_assess_date,
      last_plan_approval_date: otherDetails.last_plan_approval_date,
      medicaid_end_date: otherDetails.renewal_date,
      proId: props.pId,
      DDD_tier: otherDetails.DDD_tier,
      diagnose_code:otherDetails.diagnose_code,
      func_criteria_met:otherDetails.func_criteria_met,
      waiver_prgm:otherDetails.waiver_program,
      acquired_date: otherDetails.acquired_date,
      NJCATSelfcare:otherDetails.NJCATSelfcare,
      NJCATBehavioral:otherDetails.NJCATBehavioral,
      NJCATMedical:otherDetails.NJCATMedical,
      NJCATFCA:otherDetails.NJCATFCA,
      MedicaidEndDate:otherDetails.MedicaidEndDate,
      AgencyAssignDate:otherDetails.AgencyAssignDate,
      County:otherDetails.County,
      Race:otherDetails.Race,
      Ethnicity:otherDetails.Ethnicity,
      mailing_detail: personalDetails.mailing_detail,
      place01char: otherDetails.place01char,
      place02char: otherDetails.place02char,
      place03char: otherDetails.place03char,
      place04char: otherDetails.place04char,
      place05char: otherDetails.place05char,
      place06char: otherDetails.place06char,
      place07char: otherDetails.place07char,
      place08char: otherDetails.place08char,
      place09date: otherDetails.place09date,
      place10date: otherDetails.place10date,
      place11date: otherDetails.place11date,
      place12date: otherDetails.place12date,
      place13number: otherDetails.place13number,
      place14number: otherDetails.place14number,
      place15number: otherDetails.place15number,
      place16number: otherDetails.place16number,
      HasPet: otherDetails.HasPet,
      ProviderGenderPreference: otherDetails.ProviderGenderPreference,
      LanguagePreference: otherDetails.LanguagePreference,
      PreferRepeatProviders: otherDetails.PreferRepeatProviders,
      DriverLicenseNumber: otherDetails.DriverLicenseNumber,
      DLExpiryDate: otherDetails.DLExpiryDate,
      DLIssueDate: otherDetails.DLIssueDate,
      DLIssueState: otherDetails.DLIssueState
    };

    let msg = await axios.post( APIURL+"/customers", data);

    if (msg.data.code === 200) {
      setPersonalDetails(newState.personal_details);
      setOtherDetails(newState.other_details);
      setShowModal(true);
      setAddError(false);
    } else {
      setAddError(true);
      setShowModal(true);
    }
  }

  const getLatLong = async (add) => {
    if (add.address_line_1 !== ""
        || add.address_line_2 !== ""
        || add.city !== ""
        || add.state !== ""
        || add.country !== ""
        || add.zip !== "") {
          axios.defaults.headers = {
              'Content-Type': 'application/json',
          } 

          const instance = axios.create();
          instance.defaults.headers.common = {};
          instance.defaults.headers.common.accept = 'application/json';
          let geoCord = await instance.get(`https://dev.virtualearth.net/REST/v1/Locations?countryRegion=US&adminDistrict=${add.state}&locality=${add.city}&postalCode=${add.zip}&addressLine=${add.address_line_1}&key=${BINGAPIKEY}`);

      try{
          let latlongVal = geoCord.data.resourceSets[0].resources[0].point.coordinates;
          add.latitude = latlongVal[0]
          add.longitude = latlongVal[1]
      } catch(err){
          console.log(err)
      }
    }
  }

  const onPrimaryFormChangeHandler = (event) => {
    let oldState = { ...personalDetails, [event.target.id]: event.target.value }
    console.log(event.target.id, event.target.value);
    console.log(oldState);
    
    setPersonalDetails(oldState);
    setPersonalErrors(prev => { const errs = { ...prev }; delete errs[event.target.id]; return errs; });
    setShowAlert(false);
  }

  const onOtherDetailsFormChangeHandler = (event) => {
    let oldState = { ...otherDetails, [event.target.id]: event.target.value }
    if(event.target.id === 'status' && event.target.value !== 'Dormant') {
      oldState["TransferOutReason"] = "";
      oldState["TransferOutDate"] = "";
    }
    if(event.target.id === 'private_payer_responsible' && event.target.value === 'Self') {
      oldState[event.target.id] = null;
    }
    console.log(event.target.id, event.target.value);
    console.log(oldState);
    
    setOtherDetails(oldState);
    setOtherErrors(prev => {
      const errs = { ...prev };
      delete errs[event.target.id];
      if (event.target.id === 'status') {
        delete errs.TransferOutReason;
        delete errs.TransferOutDate;
      }
      return errs;
    });
    setShowAlert(false);
  }

  const handlePhoneListChange = (id, e) => {
    const value = e.target.value;
    const newPhnList = personalDetails.phnNumList.map(phn => (
      phn.phnId === id ? { ...phn, phone: value } : phn
    ));
    setPersonalDetails({
      ...personalDetails,
      phnNumList: personalDetails.phnNumList.map(phn => (
        phn.phnId === id ? { ...phn, phone: e.target.value } : phn
      ))
    });
    // clear phone validation error on edit
    setPersonalErrors(prev => { const errs = { ...prev }; delete errs.phone; return errs; });
  }

  const onAddPhone = () => {
    let prevState = { ...personalDetails }
    let newPhnObj = { phnId: prevState.phnNumList.length + 1, country_code: "+1", phone: "" }
    prevState.phnNumList.push(newPhnObj)
    setPersonalDetails(prev => {
      const copy = { ...prev };
      copy.phnNumList = [...prev.phnNumList, { phnId: prev.phnNumList.length + 1, country_code: '+1', phone: '' }];
      return copy;
    });
    // clear phone validation error on add
    setPersonalErrors(prev => { const errs = { ...prev }; delete errs.phone; return errs; });
  }

  const onDeletePhone = (phnId) => {
    const delPhnId = phnId;
    const prevState = { ...personalDetails };
    const newPhnList = prevState.phnNumList.filter(phn => phn.phnId !== delPhnId);
    const finalPhnList = newPhnList.map((phn, indx) => ({ ...phn, phnId: indx + 1 }));
    prevState.phnNumList = finalPhnList;
    setPersonalDetails(prev => {
      const newList = prev.phnNumList
        .filter(phn => phn.phnId !== phnId)
        .map((phn, idx) => ({ ...phn, phnId: idx + 1 }));
      return { ...prev, phnNumList: newList };
    });
    setPersonalErrors(prev => { const errs = { ...prev }; delete errs.phone; return errs; });
  }

  const handleAddressListChange = (idx, e) => {
    if (e.target.id === 'address_clear') {
        let prevState = { ...personalDetails }
        let newAddList = prevState.addressList.map((add, index) => {
            if (index === idx) {
                add.address_line_1 = ""
                add.address_line_2 = ""
                add.city = ""
                add.state = ""
                add.zip = ""
                add.latitude = ""
                add.longitude = ""
            }
            return add
        })
        prevState.addressList = newAddList
        setPersonalDetails(prevState)
        return
    }
    if (e.target.id === 'address_maps') {
        let prevState = { ...personalDetails };

        // Ensure addressList has at least idx+1 elements
        if (!Array.isArray(prevState.addressList)) {
            prevState.addressList = [];
        }
        while (prevState.addressList.length <= idx) {
            prevState.addressList.push({});
        }

        let newAddList = prevState.addressList.map((add, index) => {
            if (index === idx) {
                add.address_line_1 = 
                    e.value.result.address_components.find(c => c.types.includes("street_number")).long_name + " " +
                    e.value.result.address_components.find(c => c.types.includes("route")).long_name;

                const cityComp =
                    e.value.result.address_components.find(c => c.types.includes("locality")) ||
                    e.value.result.address_components.find(c => c.types.includes("sublocality")) ||
                    e.value.result.address_components.find(c => c.types.includes("sublocality_level_1"));
                add.city = cityComp ? cityComp.long_name : '';

                add.state = e.value.result.address_components.find(c => c.types.includes("administrative_area_level_1")).short_name;
                add.zip = e.value.result.address_components.find(c => c.types.includes("postal_code")).long_name;

                add.latitude = e.value.result.geometry.location.lat;
                add.longitude = e.value.result.geometry.location.lng;
            }
            return add;
        });

        prevState.addressList = newAddList;
        setPersonalDetails(prevState);
        return;
    }

    let prevState = { ...personalDetails }
    let newAddList = prevState.addressList.map((add, index) => {
        if (index === idx) {
            add[e.target.id] = e.target.id==='state'?e.target.value.toUpperCase():e.target.value
        }
        getLatLong(add)
        return add
    })
    prevState.addressList = newAddList
    setPersonalDetails(prevState);
  }

  const onAddAddress = () => {
    let prevState = { ...personalDetails }
    let newAddObj = { address_line_1: "", address_line_2: "", city: "", state: "", zip: "", latitude: "", longitude: "" }
    prevState.addressList.push(newAddObj)
    setPersonalDetails(prevState);
  }

  const onDeleteAddress = (idx) => {
    const newAddList = [...personalDetails.addressList];
    newAddList.splice(idx, 1);
    newAddList.forEach(add => getLatLong(add));
    
    setPersonalDetails({ ...personalDetails, addressList: newAddList });
  }

  const onRadioChangeHandler = (rValue) => {
    const oldState = { ...personalDetails }
    oldState.company_name = (rValue === "Individual")?" ":""
    oldState.first_name = (rValue === "Individual")?"":" "
    oldState.last_name = (rValue === "Individual")?"":" "
    setPersonalDetails(oldState);
    setOtherDetails({ ...otherDetails, customer_type: rValue });
  }

  const goHomeHandler = () => {
    setGoHome(true);
  };

  const addNew = () => {
    if (addError) {
      setShowModal(false);
      if (props.edit) { history.push("/projects") }
    } else {
      setShowModal(false);
      setAddError(false);
      setPersonalDetails({
        DDDID: !props.edit ? '' : props.cusDets.DDDID,
        cus_is_company: !props.edit ? false : props.cusDets.cus_is_company,
        first_name: !props.edit ? '' : !props.cusDets.cus_is_company ? (props.cusDets.cus_first_name || props.cusDets.cus_name.split(' ')[0]) : ' ',
        last_name: !props.edit ? '' : !props.cusDets.cus_is_company ? (props.cusDets.cus_last_name || props.cusDets.cus_name.split(' ').slice(1).join(' ')) : ' ',
        gender: !props.edit ? 'M' : normalizeGender(props.cusDets.gender),
        dob: !props.edit || !props.cusDets.dob ? null : props.cusDets.dob,
        company_name: !props.edit ? '' : !props.cusDets.cus_is_company ? ' ' : props.cusDets.cus_name,
        cus_local_id: !props.edit ? '' : props.cusDets.cus_local_id,
        email: !props.edit ? '' : props.cusDets.cus_email,
        addressList: !props.edit
          ? [{ address_line_1: '', address_line_2: '', city: '', state: '', zip: '', latitude: '', longitude: '' }]
          : props.cusDets.cus_address.split(':').map((add, index) => {
              let addArr = add.split(',');
              if (addArr.length > 5) {
                addArr = addArr.map((data, i) => {
                  if (i > 1) {
                    return data;
                  } else if (i < 1) {
                    return data + ',' + addArr[i + 1];
                  } else {
                    return null;
                  }
                }).filter((ele) => !!ele);
              }
              
              let location = null;
              try {
                if (addArr.length === 4) {
                  location = props.loc_list.find((loc) => (
                    loc.location_city.trim().toLowerCase() === addArr[1].trim().toLowerCase() &&
                    loc.location_zip.trim().toLowerCase() === addArr[3].trim().toLowerCase()
                  ));
                }
                if (addArr.length === 5) {
                  location = props.loc_list.find((loc) => {
                    return (
                      loc.location_addr1.trim().toLowerCase() === addArr[0].trim().toLowerCase() &&
                      loc.location_addr2.trim().toLowerCase() === addArr[1].trim().toLowerCase() &&
                      loc.location_state.trim().toLowerCase() === addArr[3].trim().toLowerCase() &&
                      loc.location_city.trim().toLowerCase() === addArr[2].trim().toLowerCase() &&
                      loc.location_zip.trim().toLowerCase() === addArr[4].trim().toLowerCase()
                    );
                  });
                }
              } catch (err) {
                location = '';
              }
              
              if (!!location) {
                let addObj = {
                  address_line_1: location.location_addr1,
                  address_line_2: location.location_addr2,
                  city: location.location_city.trim(),
                  state: location.location_state.trim(),
                  zip: location.location_zip.trim(),
                  latitude: !!location ? location.location_lat : 0,
                  longitude: !!location ? location.location_lon : 0,
                  disableFields: !props.tasklist ? false : props.tasklist.some((task) => ((task.tas_is_active === 0 || task.tas_is_active === 1) && task.tas_loc_id === location.id))
                };
                return addObj;
              } else {
                return addressFromParts(addArr);
              }
              
            }).filter((loc) => !!loc),
        phnNumList: !props.edit ? [{ phnId: 1, country_code: '+1', phone: '' }] : !!props.cusDets.cus_phone ? props.cusDets.cus_phone.split(',').map((phn, index) => {
            // Remove all non-digit characters except leading +
            let cleaned = phn.trim();
            let countryCode = '+1';
            let phoneNumber = '';
            
            if (cleaned.startsWith('+')) {
              // Extract country code (assumes +1 or +XX format)
              countryCode = cleaned.slice(0, 2);
              phoneNumber = cleaned.slice(2).replace(/\D/g, '');
            } else {
              // No country code present, clean the entire string
              phoneNumber = cleaned.replace(/\D/g, '');
            }
            
            let phnObj = { phnId: index, country_code: countryCode, phone: phoneNumber };
            return phnObj;
          }) : [{ phnId: 1, country_code: '+1', phone: '' }],
      });
      setOtherDetails({
        acquired_date: !props.edit ? moment().format('YYYY-MM-DDTHH:mm:ss') : moment(props.cusDets.cus_acquired_date).format('YYYY-MM-DDTHH:mm:ss'),
        status: !props.edit ? '' : props.cusDets.cus_status,
        customer_type: !props.edit ? 'Individual' : props.cusDets.cus_type || 'Individual',
        pManager: !props.edit ? '' : props.pManagerId,
        medicaid_id: !props.edit ? '' : props.cusDets.medicaid_id || null,
        medicaid_eligible: !props.edit ? '' : Number(props.cusDets.medicaid_eligible),
        sign_page_date: !props.edit ? '' : props.cusDets.sign_page_date || '',
        last_plan_approval_date: !props.edit ? '' : props.cusDets.last_plan_approval_date || '',
        last_assess_date: !props.edit ? '' : props.cusDets.last_assess_date || '',
        renewal_date: !props.edit ? '' : props.cusDets.renewal_date || '',
        DDD_tier: !props.edit ? '' : props.cusDets.DDD_tier || null,
        diagnose_code: !props.edit ? '' : props.cusDets.PrimaryDiagnosisCode || null,
        func_criteria_met: !props.edit ? '' : Number(props.cusDets.func_criteria_met),
        waiver_program: !props.edit ? '' : props.cusDets.waiver_program || null,
        NJCATSelfcare: !props.edit ? '' : props.cusDets.NJCATSelfcare || null,
        NJCATBehavioral: !props.edit ? '' : props.cusDets.NJCATBehavioral || null,
        NJCATMedical: !props.edit ? '' : props.cusDets.NJCATMedical || null,
        NJCATFCA: !props.edit ? '' : props.cusDets.NJCATFCA || null,
        MedicaidEndDate: !props.edit ? '' : props.cusDets.MedicaidEndDate || '',
        AgencyAssignDate: !props.edit ? '' : props.cusDets.AgencyAssignDate || '',
        County: !props.edit ? '' : props.cusDets.County || null,
        Race: !props.edit ? '' : props.cusDets.Race || null,
        Ethnicity: !props.edit ? '' : props.cusDets.Ethnicity || null,
        TransferOutReason: '',
        TransferOutDate: '',
        place01char: !props.edit ? '' : props.cusDets.place01char || null,
        place02char: !props.edit ? '' : props.cusDets.place02char || null,
        place03char: !props.edit ? '' : props.cusDets.place03char || null,
        place04char: !props.edit ? '' : props.cusDets.place04char || null,
        place05char: !props.edit ? '' : props.cusDets.place05char || null,
        place06char: !props.edit ? '' : props.cusDets.place06char || null,
        place07char: !props.edit ? '' : props.cusDets.place07char || null,
        place08char: !props.edit ? '' : props.cusDets.place08char || null,
        place09date: !props.edit ? '' : props.cusDets.place09date || null,
        place10date: !props.edit ? '' : props.cusDets.place10date || null,
        place11date: !props.edit ? '' : props.cusDets.place11date || null,
        place12date: !props.edit ? '' : props.cusDets.place12date || null,
        place13number: !props.edit ? '' : props.cusDets.place13number || null,
        place14number: !props.edit ? '' : props.cusDets.place14number || null,
        place15number: !props.edit ? '' : props.cusDets.place15number || null,
        place16number: !props.edit ? '' : props.cusDets.place16number || null
      });
      setTabValue('personal_details');
    }
  };

  if (goHome) return <Redirect to="/" />;

  if (schemaLoading) {
    return (
      <div className='d-flex flex-row w-100 justify-content-center mt-5'>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Box sx={{ textAlign: 'left', ml: 1 }}>
      {showModal ? (
        <MsgModalBox
          edit={props.edit}
          addNew={addNew}
          goHome={goHomeHandler}
          text="Customer"
          showError={addError}
          style={{ position: 'absolute' }}
          taskDates={[]}
        />
      ) : (
        <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Box component="h4">
            {props.edit ? '' : props.customerPage ? '' : 'New Customer'}
          </Box>
          {showAlert && (
            <Alert severity="info">
              {errorMsg || 'Please fill out missing form fields'}
            </Alert>
          )}
          <Tabs value={tabValue} onChange={changeTab}>
            <Tab label="Personal Details" value="personal_details" />
            <Tab label="Other Details" value="other_details" />
            {props.edit && <Tab label="Customer Tasks" value="customer_tasks" />}
          </Tabs>
          {tabValue === 'personal_details' && (
            <PersonalDetails
              errors={personalErrors}
              formVals={personalDetails}
              states={props.states}
              customerType={props.customerType}
              edit={props.edit}
              onContinue={changeTab}
              formChangeHandler={onPrimaryFormChangeHandler}
              onRadioChangeHandler={onRadioChangeHandler}
              phnNumList={personalDetails.phnNumList}
              addPhoneNumber={onAddPhone}
              deletePhoneNumber={onDeletePhone}
              handlePhoneListChange={handlePhoneListChange}
              addressList={personalDetails.addressList}
              onAddAddress={onAddAddress}
              onDeleteAddress={onDeleteAddress}
              handleAddressListChange={handleAddressListChange}
              isStateNotNJ={!!props.user.states.find(s => s.state_id !== 'NJ')}
              getFieldLabel={getFieldLabel}
              isFieldRequired={isFieldRequired}
              isFieldVisible={isFieldVisible}
              customerList={props.customerList}
            />
          )}
          {tabValue === 'other_details' && (
            <OtherDetails
              errors={otherErrors}
              formVals={otherDetails}
              status={props.customerStatus}
              customerContacts={customerContacts}
              privatePayerSupportSetting={privatePayerSupportSetting}
              user={props.user}
              edit={props.edit}
              empList={props.empList}
              customerType={props.customerType}
              formChangeHandler={onOtherDetailsFormChangeHandler}
              isStateNotNJ={!!props.user.states.find(s => s.state_id !== 'NJ')}
              getFieldLabel={getFieldLabel}
              isFieldRequired={isFieldRequired}
              isFieldVisible={isFieldVisible}
              onContinue={changeTab}
              handleSave={handleSubmit}
            />
          )}
          {tabValue === 'customer_tasks' && props.edit && (
            <CustomerTasks
              empId={props.user.emp_id}
              empName={props.user.emp_name}
              empEmail={props.user.email}
              pManagerId={props.pManagerId}
              pId={props.pId}
              cusDets={props.cusDets}
              comp_id={props.comp_id}
              DDDID={personalDetails.DDDID}
              customerType={otherDetails.customer_type}
              status={otherDetails.status}
              edit={props.edit}
              typeList={taskTypeList}
              originalSelectedTaskTypes={custTasksOriginal}
              originalDDIds={custTasksDDIds}
              onSave={handleSubmit}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CustomerForm;
